<!-- frontend/src/views/HomeView.vue -->
<template>
  <div class="home">
    <!-- HERO SECTION -->
    <section class="ct-hero-section">
      <div class="hero-background">
        <div class="hero-image-container">
          <img src="@/assets/img/hero-ct.jpg" class="hero-background-image" alt="CT Grid Platform">
          <div class="hero-gradient-overlay"></div>
        </div>
      </div>
      <div class="container ct-hero-content">
        <div class="row align-items-center">
          <div class="col-lg-6">
            <h1 class="display-4 fw-bold mb-4" v-html="t('home.hero.title')"></h1>
            <p class="lead mb-5" v-html="t('home.hero.subtitle')"></p>

            <div class="row g-3 mb-4">
              <div class="col-md-7">
                <input type="email" class="form-control ct-input" :placeholder="t('home.hero.emailPlaceholder')" v-model="email">
              </div>
              <div class="col-md-5">
                <button @click="submitDemo" class="ct-btn-primary w-100" :disabled="formSubmitting">
                  {{ t('home.hero.requestDemo') }}
                </button>
              </div>
            </div>

            <div v-if="formSubmitted" class="alert alert-success bg-transparent text-success border-success">
              {{ t('home.hero.thankYou') }}
            </div>

            <div v-if="errors.length" class="alert alert-danger bg-transparent text-danger border-danger">
              <div v-for="(error, index) in errors" :key="index">{{ error }}</div>
            </div>
          </div>
          <div class="col-lg-6 d-none d-lg-block position-relative">
            <div class="image-container">
              <div class="diamond-wrapper">
                <div class="map-container">
                </div>
                  <HeroMap />
              </div>
              <div class="vignette-effect"></div>
            </div>
          </div>
        </div>
      </div>

      <div class="ct-scroll-indicator">
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 5V19" stroke="var(--ct-orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          <path d="M19 12L12 19L5 12" stroke="var(--ct-orange)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
    </section>

    <!-- TRUSTED BY SECTION -->
    <section class="ct-section ct-gradient-section">
      <div class="container text-center">
        <h2 class="h4 mb-4">{{ t('home.trustedBy.title') }}</h2>
        <div class="markets-container">
          <span v-for="(market, index) in localizedMarkets" :key="index" class="market-item">
            {{ market.name }}
            <span class="market-tooltip">
              {{ market.description }}
            </span>
          </span>
        </div>
          <div class="ct-divider mt-5"></div>

<!--        <div class="row justify-content-center">-->
<!--          <div class="col-md-10">-->
<!--            <div class="row g-4">-->
<!--              <div class="col-4 col-md-2" v-for="index in 6" :key="index">-->
<!--                <img :src="require(`@/assets/img/client-logo${index}.png`)"-->
<!--                     class="ct-platform-logo" :alt="'Client ' + index">-->
<!--              </div>-->
<!--            </div>-->
<!--          </div>-->
<!--        </div>-->
      </div>
    </section>

    <!-- MAIN FEATURES -->
    <section class="ct-section">
      <div class="container">
        <div class="text-center mb-5">
<!--          <div class="ct-divider"></div>-->
          <h2 class="ct-title">{{ t('home.features.title') }}</h2>
        </div>

        <div class="row g-4">
          <div class="col-md-4">
            <div class="ct-card p-4 text-center">
              <div class="ct-card-icon">
                <i class="fas fa-chart-line ct-highlight"></i>
              </div>
              <h3 class="ct-feature-title">{{ t('home.features.traders.title') }}</h3>
              <p class="ct-feature-text">{{ t('home.features.traders.description') }}</p>
              <router-link to="/for-traders" class="ct-btn-outline btn-sm mt-3">{{ t('home.features.traders.cta') }}</router-link>
            </div>
          </div>
          <div class="col-md-4">
            <div class="ct-card p-4 text-center">
              <div class="ct-card-icon">
                <i class="fas fa-users ct-highlight"></i>
              </div>
              <h3 class="ct-feature-title">{{ t('home.features.brokers.title') }}</h3>
              <p class="ct-feature-text">{{ t('home.features.brokers.description') }}</p>
              <router-link to="/for-brokers" class="ct-btn-outline btn-sm mt-3">{{ t('home.features.brokers.cta') }}</router-link>
            </div>
          </div>
          <div class="col-md-4">
            <div class="ct-card p-4 text-center">
              <div class="ct-card-icon">
                <i class="fas fa-shield-alt ct-highlight"></i>
              </div>
              <h3 class="ct-feature-title">{{ t('home.features.riskManagement.title') }}</h3>
              <p class="ct-feature-text">{{ t('home.features.riskManagement.description') }}</p>
              <router-link to="/for-risk-managers" class="ct-btn-outline btn-sm mt-3">{{ t('home.features.riskManagement.cta') }}</router-link>
            </div>
          </div>
        </div>
        <div class="text-center mt-5">
          <div class="ct-divider"></div>
        </div>
      </div>
    </section>

    <!-- PRODUCT SHOWCASE -->
    <section class="ct-section ct-dark-secondary-section">
      <div class="container">
        <div class="row align-items-center">
          <div class="col-lg-6 mb-5 mb-lg-0">
            <img src="@/assets/img/excelmobile.png" class="img-fluid rounded-3" alt="CT Grid Product">
          </div>
          <div class="col-lg-5 offset-lg-1">
            <h2 class="ct-title">{{ t('home.productShowcase.title') }}</h2>
            <p class="ct-subtitle">{{ t('home.productShowcase.subtitle') }}</p>

            <div class="d-flex flex-column gap-4">
              <div class="d-flex" v-for="(capability, index) in topCapabilities" :key="index">
                <div style="color: var(--ct-orange); margin-right: 1rem;">
                  <i :class="capability.icon" style="font-size: 1.5rem;"></i>
                </div>

                <div>
                  <h4 class="h5 mb-2">{{ t(`home.capabilities.${capability.key}.title`) }}</h4>
                  <p class="text-muted">{{ t(`home.capabilities.${capability.key}.description`) }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- PROBLEMS WE SOLVE -->
    <section class="ct-section position-relative ct-dark-section">
    <div class="parallax-background" ref="chartBackground">
      <img :src="bgChartImage" class="parallax-bg-image" alt="Chart Background">
      <div class="parallax-gradient-overlay"></div>
    </div>
      <div class="container position-relative">
        <div class="row justify-content-center text-center">
          <div class="col-lg-8">
            <h2 class="ct-title mb-4" v-html="t('home.problemWeSolve.title')"></h2>
            <p class="lead mb-5" v-html="t('home.problemWeSolve.subtitle')"></p>
            <router-link to="/contact" class="ct-btn-primary">{{ t('home.problemWeSolve.cta') }}</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- CAPABILITIES -->
    <section class="ct-section ct-dark-tertiary-section">
      <div class="container">
        <div class="text-center mb-5">
          <div class="ct-divider"></div>
          <h2 class="ct-title">{{ t('home.capabilities.title') }}</h2>
        </div>

        <div class="row g-4">
          <div class="col-md-4" v-for="capability in capabilityItems" :key="capability.key">
            <div class="text-center mb-4">
              <div class="ct-icon">
                <i :class="capability.icon"></i>
              </div>
              <h3 class="h5 ct-highlight mb-3">{{ t(`home.capabilities.${capability.key}.title`) }}</h3>
              <p class="text-muted">{{ t(`home.capabilities.${capability.key}.description`) }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CALL TO ACTION -->
    <section class="ct-section bg-black">
      <div class="container text-center">
        <h2 class="ct-title mb-4">{{ t('home.cta.title') }}</h2>
        <p class="lead mb-5">{{ t('home.cta.subtitle') }}</p>

        <div class="row justify-content-center">
          <div class="col-lg-8">
            <form @submit.prevent="submitFullDemo" class="row g-3">
              <div class="col-md-4">
                <input type="text" class="form-control ct-input form-control-lg h-100" :placeholder="t('home.cta.namePlaceholder')" v-model="name" required>
              </div>
              <div class="col-md-5">
                <input type="email" class="form-control ct-input form-control-lg h-100" :placeholder="t('home.hero.emailPlaceholder')" v-model="fullEmail" required>
              </div>
              <div class="col-md-3">
                <button type="submit" class="ct-btn-primary w-100 h-100">{{ t('home.cta.getStarted') }}</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useI18n } from 'vue-i18n'
import { mapActions } from 'vuex'
import HeroMap from '@/components/HeroMap.vue';

export default {
  name: 'HomeView',
  components: {
    HeroMap
  },
  setup() {
    const { t, locale } = useI18n()
    return { t, locale }
  },
  data() {
    return {
      bgChartImage: require('@/assets/img/bg-chart.jpg'),
      email: '',
      fullEmail: '',
      name: '',
      formSubmitting: false,
      formSubmitted: false,
      errors: [],
      parallaxAmount: 0,
      chartParallaxAmount: 0,
      markets: [
        {
          name: 'Crude Complex',
          description: 'NA EU APEC • Crude, NGLs, Naphtha, Gasoline, Distillates, Fuel Oil, Petchems'
        },
        {
          name: 'Natural Gas',
          description: 'JKM, JKM/TFU, TFU, NBP'
        },
        {
          name: 'Agricultural',
          description: 'Oil, Meal, Crush • Biodiesel, Ethanol, Rapeseed, Sunflower, Palm'
        },
        {
          name: 'Power',
          description: 'Japanese Power, Spark Spreads'
        },
        {
          name: 'Freight',
          description: 'Wet Freight, Dry Bulk'
        },
        {
          name: 'Metals',
          description: 'Battery Metals, Iron Ore'
        },
        {
          name: 'FX',
          description: 'FX Forward curve alignment with commodity contracts'
        }
      ],
      capabilityItems: [
        {
          key: 'internalWhiteboard',
          icon: 'fas fa-th'
        },
        {
          key: 'syncCurves',
          icon: 'fas fa-project-diagram'
        },
        {
          key: 'realTimeData',
          icon: 'fas fa-clock'
        },
        {
          key: 'efficientHedging',
          icon: 'fas fa-shield-alt'
        },
        {
          key: 'automatedReporting',
          icon: 'fas fa-file-alt'
        },
        {
          key: 'directIM',
          icon: 'fas fa-comment'
        }
      ]
    };
  },
  computed: {
    topCapabilities() {
      return this.capabilityItems.slice(0, 3);
    },
    localizedMarkets() {
      return [
        {
          name: this.t('markets.crudeComplex.name'),
          description: this.t('markets.crudeComplex.description')
        },
        {
          name: this.t('markets.naturalGas.name'),
          description: this.t('markets.naturalGas.description')
        },
        {
          name: this.t('markets.agricultural.name'),
          description: this.t('markets.agricultural.description')
        },
        {
          name: this.t('markets.power.name'),
          description: this.t('markets.power.description')
        },
        {
          name: this.t('markets.freight.name'),
          description: this.t('markets.freight.description')
        },
        {
          name: this.t('markets.metals.name'),
          description: this.t('markets.metals.description')
        },
        {
          name: this.t('markets.fx.name'),
          description: this.t('markets.fx.description')
        }
      ];
    }
  },
  methods: {
    ...mapActions('enquiries', ['submitEnquiry']),
    handleScroll() {
      const heroSection = document.querySelector('.ct-hero-section');
      const heroHeight = heroSection.offsetHeight;
      const scrollPosition = window.scrollY;

      // Only apply parallax effect while within the hero section
      if (scrollPosition <= heroHeight) {
        const parallaxImage = document.querySelector('.hero-background-image');
        if (parallaxImage) {
          // Calculate the amount to move the background based on scroll position
          // Starting from 30% down and moving up as you scroll
          this.parallaxAmount = scrollPosition * 0.1;
          parallaxImage.style.transform = `translate(-10%, ${5 - this.parallaxAmount}%) scale(1.2)`;
        }
      }

      // Handle chart section parallax
      const chartSection = document.querySelector('.ct-dark-section');
      if (chartSection) {
        const chartRect = chartSection.getBoundingClientRect();
        const windowHeight = window.innerHeight;

        // Check if section is visible in viewport
        if (chartRect.top < windowHeight && chartRect.bottom > 0) {
          const chartParallaxImage = document.querySelector('.parallax-bg-image');
          if (chartParallaxImage) {
            // Calculate how far the section is in the viewport (0 to 1)
            // 0 = just entered viewport from bottom
            // 0.5 = middle of section is at middle of viewport
            // 1 = just about to exit viewport at top
            const visiblePercentage = 1 - (chartRect.top / windowHeight);

            // Limit the range to 0-1
            const limitedPercentage = Math.max(0, Math.min(1, visiblePercentage));

            // Calculate parallax movement (20% total movement)
            this.chartParallaxAmount = 20 * limitedPercentage;

            // Apply the transform - starting at -10% and moving up
            chartParallaxImage.style.transform = `translate(0, ${30 - this.chartParallaxAmount}%) scale(1.2)`;
          }
        }
      }
    },
    submitDemo() {
      if (!this.email) {
        this.errors = [this.t('errors.required', { field: this.t('home.hero.emailPlaceholder') })];
        return;
      }

      this.$router.push({
        path: `/${this.locale}/contact`,
        query: {
          email: this.email
        }
      });
    },

    submitFullDemo() {
      if (!this.name || !this.fullEmail) {
        this.errors = [this.t('errors.required', { field: this.t('home.cta.namePlaceholder') })];
        return;
      }

      this.$router.push({
        path: `/${this.locale}/contact`,
        query: {
          name: this.name,
          email: this.fullEmail
        }
      });
    }
  },
  created() {
    this.setPageTitle(this.t('navigation.home'));
  },
  mounted() {
    console.log("Using composition API i18n:", this.t('home.hero.title'));
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
  }
};
</script>

<style scoped>
/* Hero section with background image */
.ct-hero-section {
  position: relative;
  padding: 8rem 0 6rem;
  overflow: hidden;
  background-attachment: fixed; /* This is key for the parallax effect */
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-color: var(--ct-dark);
}

.hero-background-image {
  position: absolute;
  opacity: 0.5;
  min-width: 150%; /* Increase from 100% to ensure coverage during scrolling */
  min-height: 150%; /* Increase from 100% to ensure coverage during scrolling */
  width: auto;
  height: auto;
  object-fit: cover;
  will-change: transform;
  /* Initial position showing more of the bottom of the image */
  transform: translate(-10%, -5%) scale(1.2);
  left: 0;
  top: 0;
  pointer-events: none;
}

/* Add this to prevent any white space from showing */
.hero-image-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  overflow: hidden; /* Make sure overflow is hidden */
  background-color: var(--ct-dark); /* Match background color */
}

/* Ensure container has background as fallback */
.hero-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  background-color: var(--ct-dark); /* Match background color */
}

.hero-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(18, 20, 23, 0.7) 0%, rgba(18, 20, 23, 0.9) 70%);
  z-index: 1;
}

.ct-hero-content {
  position: relative;
  z-index: 2;
}

.ct-hero-content h1,
.ct-hero-content p {
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  color: #fff;
}

.ct-dark-section {
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.parallax-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  overflow: hidden;
  background-color: var(--ct-dark);
}

.parallax-bg-image {
  position: absolute;
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.2;
  transform: translate(-10%, 30%) scale(1.2);
  will-change: transform;
}

.parallax-gradient-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at center, rgba(18, 20, 23, 0.5) 0%, rgba(18, 20, 23, 0.8) 70%);
  z-index: 1;
}

/* Make sure container content is above the background */
.ct-dark-section .container {
  position: relative;
  z-index: 2;
}

/* Add a glow effect to the highlight text */
.ct-highlight {
  color: var(--ct-orange);
  text-shadow: 0 0 10px rgba(253, 153, 0, 0.5);
  position: relative;
}

/* Add a subtle animation to the highlighted text */
@keyframes glow {
  0% { text-shadow: 0 0 5px rgba(253, 153, 0, 0.3); }
  50% { text-shadow: 0 0 15px rgba(253, 153, 0, 0.6); }
  100% { text-shadow: 0 0 5px rgba(253, 153, 0, 0.3); }
}

.ct-highlight {
  animation: glow 3s infinite;
}

.image-container {
  position: relative;
  display: inline-block;
  width: 100%;
  right: -50px;
  height: 400px; /* Adjust height as needed */
}

.diamond-wrapper {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-60%, -55%) rotate(-10deg);
  width: 130%; /* Adjust size as needed */
}

.vignette-effect {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  filter: drop-shadow(0 0 30px rgba(0,0,0,0.8));
  z-index: -1;
}

.markets-container {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.5rem 1rem;
  max-width: 800px;
  margin: 0 auto;
}

.market-item {
  position: relative;
  color: var(--ct-light-gray);
  cursor: pointer;
  padding: 2px 5px;
  border-radius: 4px;
  transition: color 0.3s ease;
}

.market-item:hover {
  color: var(--ct-orange);
}

.market-tooltip {
  visibility: hidden;
  width: 250px;
  background-color: var(--ct-dark-tertiary);
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 10px;
  position: absolute;
  z-index: 1;
  top: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  box-shadow: 0 5px 15px rgba(0,0,0,0.3);
  pointer-events: none;
  font-size: 0.9rem;
  line-height: 1.4;
  border: 1px solid rgba(255,255,255,0.1);
}

.market-tooltip::after {
  content: "";
  position: absolute;
  bottom: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent var(--ct-dark-tertiary) transparent;
}

.market-item:hover .market-tooltip {
  visibility: visible;
  opacity: 1;
}
.map-container {
  position: relative;
}
</style>
